import { API_BASE } from "../utils/api";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function fmt(n) {
  return "R " + Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtPct(n) {
  const sign = n >= 0 ? "+" : "";
  return `${sign}${Number(n || 0).toFixed(1)}%`;
}
function pct(part, total) {
  if (!total) return 0;
  return Math.min(100, Math.round((part / total) * 100));
}

// ── Simple bar component ───────────────────────────────────────────────────
function Bar({ value, max, color = "bg-luxury-cta", height = "h-2" }) {
  const w = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className={`w-full ${height} rounded-full bg-luxury-elevated overflow-hidden`}>
      <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${w}%` }} />
    </div>
  );
}

// ── Score ring ─────────────────────────────────────────────────────────────
function ScoreRing({ score }) {
  const color = score >= 80 ? "text-luxury-cta" : score >= 60 ? "text-luxury-gold" : score >= 40 ? "text-amber-500" : "text-red-500 dark:text-red-400";
  const label = score >= 80 ? "Excellent" : score >= 60 ? "Good" : score >= 40 ? "Fair" : "Needs Work";
  return (
    <div className="flex flex-col items-center">
      <div className={`text-5xl font-bold ${color}`}>{score}</div>
      <div className={`text-sm font-semibold mt-1 ${color}`}>{label}</div>
      <div className="text-xs text-luxury-muted mt-0.5">out of 100</div>
    </div>
  );
}

export default function Analytics() {
  const { token }    = useAuth();
  const navigate     = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [docsRes, statsRes, debtRes, invRes] = await Promise.all([
        fetch(`${API_BASE}/api/documents/`,               { headers }),
        fetch(`${API_BASE}/api/documents/stats/summary`,  { headers }),
        fetch(`${API_BASE}/api/debt/`,                    { headers }),
        fetch(`${API_BASE}/api/investments/`,              { headers }),
      ]);

      const docs    = docsRes.ok    ? (await docsRes.json()).documents || []       : [];
      const stats   = statsRes.ok   ? await statsRes.json()                        : {};
      const debtD   = debtRes.ok    ? await debtRes.json()                         : {};
      const invD    = invRes.ok     ? await invRes.json()                          : {};

      setData({ docs, stats, debtSummary: debtD.summary || {}, invSummary: invD.summary || {}, debts: debtD.debts || [], investments: invD.investments || [] });
    } catch (err) {
      console.error(err);
      // Safe fallback so page never crashes when backend is offline
      setData({ docs: [], stats: {}, debtSummary: {}, invSummary: {}, debts: [], investments: [] });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return (
    <div className="min-h-screen bg-luxury-bg flex items-center justify-center">
      <div className="text-center">
        <svg className="animate-spin w-8 h-8 text-luxury-gold mx-auto mb-3" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
        </svg>
        <p className="text-luxury-secondary text-sm">Loading your financial overview...</p>
      </div>
    </div>
  );

  // Safe destructure — always defined (fallback set in catch above)
  const docs        = data?.docs        || [];
  const stats       = data?.stats       || {};
  const debtSummary = data?.debtSummary || {};
  const invSummary  = data?.invSummary  || {};
  const debts       = data?.debts       || [];
  const investments = data?.investments || [];

  // ── Financial Health Score ────────────────────────────────────────────────
  let score = 50; // base
  const scoreBreakdown = [];

  // Documents score (0–20): having key documents
  const hasDocuments = (docs?.length || 0) > 0;
  const docScore = Math.min(20, (docs?.length || 0) * 4);
  score += docScore - 10; // adjust from base
  scoreBreakdown.push({
    label: "Document Vault",
    value: docScore,
    max: 20,
    desc: hasDocuments ? `${docs?.length} documents stored` : "No documents uploaded yet",
    ok: hasDocuments,
  });

  // Debt score (0–30): lower debt = higher score
  const totalDebt   = debtSummary?.total_balance  ?? 0;
  const totalInvest = invSummary?.total_value      ?? 0;
  const debtScore   = totalDebt === 0 ? 30 : totalInvest > totalDebt ? 25 : totalInvest > totalDebt * 0.5 ? 15 : 5;
  score += debtScore - 15;
  scoreBreakdown.push({
    label: "Debt vs Investments",
    value: debtScore,
    max: 30,
    desc: totalDebt === 0 ? "No active debts tracked" : totalInvest > totalDebt ? "Investments exceed debt — strong position" : `${fmt(totalDebt)} in active debt`,
    ok: totalDebt === 0 || totalInvest > totalDebt,
  });

  // Investment score (0–25): having investments
  const invScore = totalInvest > 0 ? Math.min(25, Math.round((totalInvest / 100000) * 10) + 10) : 0;
  score += invScore - 10;
  scoreBreakdown.push({
    label: "Investment Portfolio",
    value: invScore,
    max: 25,
    desc: totalInvest > 0 ? `${fmt(totalInvest)} portfolio value` : "No investments tracked yet",
    ok: totalInvest > 0,
  });

  // SARS readiness (0–25): having tax return docs
  const taxReturnDocs = stats?.by_category?.tax_return || 0;
  const sarsScore     = Math.min(25, taxReturnDocs * 10);
  score += sarsScore - 10;
  scoreBreakdown.push({
    label: "SARS Filing Readiness",
    value: sarsScore,
    max: 25,
    desc: taxReturnDocs > 0 ? `${taxReturnDocs} tax return document${taxReturnDocs !== 1 ? "s" : ""} uploaded` : "No tax return documents yet",
    ok: taxReturnDocs > 0,
  });

  score = Math.max(0, Math.min(100, Math.round(score)));

  // ── Net Worth estimate ────────────────────────────────────────────────────
  const netWorth   = totalInvest - totalDebt;
  const netWorthOk = netWorth >= 0;

  // ── Document breakdown ────────────────────────────────────────────────────
  const docCategories = [
    { label: "Tax Returns",    key: "tax_return",    color: "bg-luxury-gold" },
    { label: "Invoices",       key: "invoice",       color: "bg-amber-500" },
    { label: "Bank Statements",key: "bank_statement",color: "bg-purple-500" },
    { label: "Payslips",       key: "payslip",       color: "bg-luxury-cta" },
    { label: "Expenses",       key: "expense",       color: "bg-red-500" },
    { label: "Contracts",      key: "contract",      color: "bg-blue-500" },
  ].map(c => ({ ...c, count: stats?.by_category?.[c.key] || 0 }))
   .filter(c => c.count > 0);

  const totalDocCount = docs?.length || 0;

  return (
    <div className="min-h-screen bg-luxury-bg p-6 lg:p-8">

      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-6 mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-8 h-8 rounded-lg bg-luxury-elevated border border-luxury-gold/30 flex items-center justify-center">
            <svg className="w-4 h-4 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <span className="text-xs font-semibold uppercase tracking-wider text-luxury-muted">Financial Overview</span>
        </div>
        <h1 className="text-2xl font-bold text-luxury-text">Analytics & Insights</h1>
        <p className="text-sm text-luxury-secondary mt-1">Your complete financial health overview across all TaxVault modules.</p>
      </div>

      {/* ── Financial Health Score ───────────────────────────────── */}
      <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-6 mb-6">
        <h2 className="font-bold text-luxury-text text-sm mb-5 uppercase tracking-wide">Financial Health Score</h2>
        <div className="flex flex-col sm:flex-row items-center gap-8">
          <ScoreRing score={score} />
          <div className="flex-1 w-full space-y-4">
            {scoreBreakdown.map(s => (
              <div key={s.label}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center text-xs ${s.ok ? "bg-luxury-cta/10 text-luxury-cta" : "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400"}`}>
                      {s.ok ? "✓" : "!"}
                    </span>
                    <span className="text-xs font-semibold text-luxury-text">{s.label}</span>
                  </div>
                  <span className="text-xs text-luxury-muted">{s.value}/{s.max}</span>
                </div>
                <Bar value={s.value} max={s.max} color={s.ok ? "bg-luxury-cta" : "bg-amber-400"} />
                <p className="text-xs text-luxury-muted mt-1">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Net Worth + Key Numbers ──────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Estimated Net Worth",   value: fmt(netWorth),                              color: netWorthOk ? "text-luxury-cta" : "text-red-600 dark:text-red-400",    accent: netWorthOk ? "border-t-luxury-cta" : "border-t-red-400" },
          { label: "Investment Portfolio",  value: fmt(totalInvest),                           color: "text-luxury-gold",                                                    accent: "border-t-luxury-gold" },
          { label: "Total Debt",            value: fmt(totalDebt),                             color: totalDebt > 0 ? "text-red-600 dark:text-red-400" : "text-luxury-cta", accent: totalDebt > 0 ? "border-t-red-400" : "border-t-luxury-cta" },
          { label: "Tax Documents",         value: totalDocCount,                              color: "text-luxury-text",                                                    accent: "border-t-luxury-border" },
        ].map(s => (
          <div key={s.label} className={`rounded-2xl p-5 border border-luxury-border border-t-2 bg-luxury-surface hover:scale-[1.02] transition-all ${s.accent}`}>
            <div className={`text-xl lg:text-2xl font-bold mb-1 ${s.color}`}>{s.value}</div>
            <div className="text-xs font-medium text-luxury-secondary uppercase tracking-wide">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

        {/* ── Investment Breakdown ──────────────────────────────── */}
        <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-luxury-text text-sm">Investment Portfolio</h3>
            <button onClick={() => navigate("/investments")} className="text-xs text-luxury-gold hover:text-luxury-gold/80 font-medium">View all →</button>
          </div>
          {investments.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-luxury-muted text-xs mb-3">No investments tracked yet</p>
              <button onClick={() => navigate("/investments")} className="text-xs px-3 py-1.5 rounded-lg bg-luxury-cta text-white font-semibold">Add Investment</button>
            </div>
          ) : (
            <div className="space-y-3">
              {investments.slice(0, 5).map(inv => (
                <div key={inv.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-base">{
                      { shares:"📈", etf:"📊", unit_trust:"🏦", retirement_ra:"🏛️", tfsa:"🆓",
                        property:"🏠", crypto:"₿", cash_savings:"💵", other:"📋" }[inv.investment_type] || "📋"
                    }</span>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-luxury-text truncate">{inv.name}</p>
                      {inv.institution && <p className="text-xs text-luxury-muted truncate">{inv.institution}</p>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-3">
                    <p className="text-xs font-bold text-luxury-gold">{fmt(inv.current_value)}</p>
                    <p className={`text-xs font-semibold ${inv.gain_loss >= 0 ? "text-luxury-cta" : "text-red-600 dark:text-red-400"}`}>
                      {fmtPct(inv.gain_loss_pct)}
                    </p>
                  </div>
                </div>
              ))}
              {investments.length > 5 && (
                <p className="text-xs text-luxury-muted text-center pt-1">+{investments.length - 5} more</p>
              )}
              <div className="pt-3 border-t border-luxury-border flex justify-between">
                <span className="text-xs font-semibold text-luxury-secondary">Total Return</span>
                <span className={`text-xs font-bold ${(invSummary.gain_loss_pct || 0) >= 0 ? "text-luxury-cta" : "text-red-600 dark:text-red-400"}`}>
                  {fmtPct(invSummary.gain_loss_pct || 0)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* ── Debt Breakdown ───────────────────────────────────── */}
        <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-luxury-text text-sm">Credit & Debt</h3>
            <button onClick={() => navigate("/credit-debt")} className="text-xs text-luxury-gold hover:text-luxury-gold/80 font-medium">View all →</button>
          </div>
          {debts.filter(d => !d.is_paid_off).length === 0 ? (
            <div className="text-center py-6">
              <p className="text-luxury-muted text-xs mb-1">
                {debts.length > 0 ? "🎉 All debts paid off!" : "No debts tracked"}
              </p>
              {debts.length === 0 && (
                <button onClick={() => navigate("/credit-debt")} className="text-xs px-3 py-1.5 rounded-lg bg-luxury-elevated border border-luxury-border text-luxury-secondary font-semibold mt-2">Add Debt</button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {debts.filter(d => !d.is_paid_off).slice(0, 5).map(debt => {
                const paidPct = debt.total_balance > 0 ? pct(debt.total_balance - debt.current_balance, debt.total_balance) : 0;
                return (
                  <div key={debt.id}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-base">{{ credit_card:"💳", personal_loan:"🏦", home_loan:"🏠", car_loan:"🚗", student_loan:"🎓", store_account:"🛍️", overdraft:"⚠️", other:"📋" }[debt.debt_type] || "📋"}</span>
                        <p className="text-xs font-semibold text-luxury-text truncate">{debt.name}</p>
                      </div>
                      <p className="text-xs font-bold text-red-600 dark:text-red-400 flex-shrink-0 ml-2">{fmt(debt.current_balance)}</p>
                    </div>
                    <Bar value={paidPct} max={100} color="bg-luxury-cta" />
                    <p className="text-xs text-luxury-muted mt-0.5">{paidPct}% paid off · {debt.interest_rate}% p.a.</p>
                  </div>
                );
              })}
              <div className="pt-3 border-t border-luxury-border flex justify-between">
                <span className="text-xs font-semibold text-luxury-secondary">Total Outstanding</span>
                <span className="text-xs font-bold text-red-600 dark:text-red-400">{fmt(debtSummary.total_balance)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Document Breakdown ───────────────────────────────────── */}
      {totalDocCount > 0 && (
        <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-luxury-text text-sm">Tax Document Breakdown</h3>
            <button onClick={() => navigate("/documents")} className="text-xs text-luxury-gold hover:text-luxury-gold/80 font-medium">View all →</button>
          </div>
          {docCategories.length === 0 ? (
            <p className="text-xs text-luxury-muted text-center py-4">No categorised documents yet</p>
          ) : (
            <div className="space-y-3">
              {docCategories.map(c => (
                <div key={c.key}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-luxury-secondary">{c.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-luxury-text">{c.count}</span>
                      <span className="text-xs text-luxury-muted">{pct(c.count, totalDocCount)}%</span>
                    </div>
                  </div>
                  <Bar value={c.count} max={totalDocCount} color={c.color} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Recommendations ──────────────────────────────────────── */}
      <div className="rounded-2xl bg-luxury-surface border border-luxury-border p-5">
        <h3 className="font-semibold text-luxury-text text-sm mb-4">💡 Personalised Recommendations</h3>
        <div className="space-y-3">
          {[
            totalDocCount === 0 && {
              icon: "📁", text: "Upload your tax documents to get started.", action: () => navigate("/upload"), actionLabel: "Upload Now", color: "border-luxury-gold/30 bg-yellow-50 dark:bg-luxury-gold/5",
            },
            (stats?.by_category?.tax_return || 0) === 0 && {
              icon: "📋", text: "No ITR12 tax return documents uploaded. Add them before Filing Season opens on 1 July.", action: () => navigate("/upload"), actionLabel: "Upload ITR12", color: "border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-900/10",
            },
            totalInvest === 0 && {
              icon: "📈", text: "Start tracking your investments to build your net worth picture.", action: () => navigate("/investments"), actionLabel: "Add Investment", color: "border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-900/10",
            },
            totalDebt > 0 && totalInvest < totalDebt && {
              icon: "⚠️", text: "Your debt exceeds your investments. Consider increasing monthly investment contributions.", action: () => navigate("/budget"), actionLabel: "Review Budget", color: "border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-900/10",
            },
            score >= 80 && {
              icon: "🏆", text: "Excellent financial health! Keep it up — consider maximising your TFSA contributions (R36,000/year tax-free).", action: null, actionLabel: null, color: "border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-900/10",
            },
          ].filter(Boolean).slice(0, 4).map((rec, i) => rec && (
            <div key={i} className={`rounded-xl border px-4 py-3 flex items-center justify-between gap-3 ${rec.color}`}>
              <div className="flex items-center gap-3">
                <span className="text-xl flex-shrink-0">{rec.icon}</span>
                <p className="text-xs text-luxury-secondary">{rec.text}</p>
              </div>
              {rec.action && (
                <button onClick={rec.action} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-luxury-cta hover:bg-luxury-cta-hover text-white transition-colors flex-shrink-0">
                  {rec.actionLabel}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
